import type { DashboardRow, PeriodColumn } from './types';

export function formatNumberPdf(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('es-CR', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(value));
}

export function formatVariationPdf(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—';
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
}

export function getNumeric(
  row: DashboardRow | undefined,
  columnKey: string
): number | null {
  if (!row) return null;
  const raw = row[columnKey];
  if (raw === null || raw === undefined || raw === '') return null;
  const num = Number(raw);
  return Number.isNaN(num) ? null : num;
}

export function calcVariation(current: number, previous: number): number | null {
  if (previous === 0 || !Number.isFinite(previous)) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

export function getComparisonPeriods(periodColumns: PeriodColumn[]) {
  const realColumns = periodColumns.filter(c => !c.isBudget && c.date);
  const latestReal = realColumns[realColumns.length - 1];
  const previousReal = realColumns[realColumns.length - 2];

  return { latestReal, previousReal, realColumns };
}

export function formatPeriodLabel(col?: PeriodColumn): string {
  if (!col) return '—';
  return col.yearMonth;
}

export function findBudgetColumn(
  periodColumns: PeriodColumn[],
  realColumn: PeriodColumn | undefined
): PeriodColumn | undefined {
  if (!realColumn) return undefined;
  return periodColumns.find(
    c => c.isBudget && c.yearMonth === realColumn.yearMonth
  );
}

export function getLatestVariation(
  row: DashboardRow | undefined,
  periodColumns: PeriodColumn[]
): number | null {
  const realColumns = periodColumns.filter(c => !c.isBudget && c.date);
  const latest = realColumns[realColumns.length - 1];
  const previous = realColumns[realColumns.length - 2];
  if (!latest || !previous || !row) return null;

  const current = getNumeric(row, latest.key);
  const prev = getNumeric(row, previous.key);
  if (current === null || prev === null) return null;
  return calcVariation(current, prev);
}

export function getLatestBudget(
  row: DashboardRow | undefined,
  periodColumns: PeriodColumn[]
): number | null {
  const realColumns = periodColumns.filter(c => !c.isBudget && c.date);
  const latest = realColumns[realColumns.length - 1];
  const budgetCol = findBudgetColumn(periodColumns, latest);
  if (!budgetCol || !row) return null;
  return getNumeric(row, budgetCol.key);
}

export function getReportColumns(
  periodColumns: PeriodColumn[],
  selectedMonths: string[]
): PeriodColumn[] {
  const realColumns = periodColumns.filter(
    c => !c.isBudget && c.date && selectedMonths.includes(c.yearMonth)
  );

  // Incluir presupuesto del último mes seleccionado si existe
  const latestSelected = realColumns[realColumns.length - 1];
  const budgetCol = findBudgetColumn(periodColumns, latestSelected);

  if (budgetCol) {
    return [...realColumns, budgetCol];
  }

  return realColumns;
}

import type { PnLTreeNode } from './types';

/**
 * Filtra el árbol del P&L para respetar la vista de gastos de operación
 * seleccionada (detalle o clasificación). El total de la sección siempre
 * se conserva.
 */
export function filterTreeByGastosView(
  tree: PnLTreeNode[],
  gastosView?: 'detalle' | 'clasificacion'
): PnLTreeNode[] {
  if (!gastosView) return tree;

  return tree.map(section => {
    if (section.id !== 'gastos-operacion') return section;
    if (!section.children) return section;

    const filtered = section.children.filter(
      child => child.groupMode === 'all' || child.groupMode === gastosView
    );

    return {
      ...section,
      children: filtered,
    };
  });
}
